/**
 * TrayectoriaSection
 * Funnel acumulado (Ofertas → Solicitudes → Desembolsos → Cobrado) +
 * salud crediticia por crédito + próxima cuota + insights.
 * Reemplaza al tab "Auditoría". El historial de accesos se mueve a Identidad.
 */
import { useMemo } from 'react';
import { Lightbulb, AlertTriangle, ChevronRight, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { useBackofficeOffers } from '@/features/credit/hooks/useBackofficeOffers';
import { useBackofficeApplications } from '@/features/credit/hooks/useBackofficeApplications';
import { useBackofficeCredits, useFinancialProfile } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import {
  creditScore,
  scoreColor,
  statusBadge,
  daysUntil,
  computeOverdueAmount,
  computeHealthGlobal,
  computeTendency,
  LegendDot,
  type Tendency,
} from '@/features/credit/utils/creditHealth';
import type {
  BackofficeOffersListItem,
  BackofficeApplicationsListItem,
  BackofficeCreditsListItem,
} from '@/types/backoffice-crud.types';

interface TrayectoriaSectionProps {
  directoryId: string;
}

// ── Main ──────────────────────────────────────────────────────────────────

export function TrayectoriaSection({ directoryId }: TrayectoriaSectionProps) {
  const { profile } = useFinancialProfile(directoryId);
  const { offers, isLoading: offersLoading } = useBackofficeOffers();
  const { applications, isLoading: appsLoading } = useBackofficeApplications();
  const { credits, isLoading: creditsLoading } = useBackofficeCredits();

  const isLoading = offersLoading || appsLoading || creditsLoading;

  const clientOffers = useMemo(() => offers.filter((o) => o.directory_id === directoryId), [offers, directoryId]);
  const clientApps = useMemo(() => applications.filter((a) => a.directory_id === directoryId), [applications, directoryId]);
  const clientCredits = useMemo(() => credits.filter((c) => c.directory_id === directoryId), [credits, directoryId]);

  if (isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">Cargando trayectoria…</div>;
  }

  // Funnel sums
  const totalOffers = clientOffers.reduce((s, o) => s + (o.max_amount_mxn ?? 0), 0);
  const signedApps = clientApps.filter((a) => {
    const s = a.status?.toUpperCase();
    return s === 'SIGNED' || s === 'DISBURSED';
  });
  const totalApps = signedApps.reduce((s, a) => s + (a.amount_mxn ?? 0), 0);
  const totalDisbursed = clientCredits.reduce((s, c) => s + (c.amount_mxn ?? 0), 0);
  const totalCobrado = clientCredits.reduce(
    (s, c) => s + (c.weeks_paid ?? 0) * (c.weekly_payment_mxn ?? 0),
    0,
  );
  const totalOverdue = clientCredits.reduce((s, c) => s + computeOverdueAmount(c), 0);
  const totalFuture = clientCredits.reduce((s, c) => {
    const remaining = (c.weeks_total ?? c.term_weeks ?? 0) - (c.weeks_paid ?? 0);
    const remainingAmount = Math.max(0, remaining * (c.weekly_payment_mxn ?? 0));
    return s + Math.max(0, remainingAmount - computeOverdueAmount(c));
  }, 0);
  const totalPending = totalFuture + totalOverdue;

  // Drop-offs
  const offerToAppDrop = totalOffers - totalApps;
  const appToCreditDrop = totalApps - totalDisbursed;

  // Health
  const healthGlobal = computeHealthGlobal(clientCredits);
  const tendency = computeTendency(clientCredits);

  // Capacidad
  const capacity = profile?.max_credit_amount_mxn ?? 0;

  // Próxima cuota
  const nextPayment = findNextPayment(clientCredits);

  // Insights
  const insights = buildInsights(clientOffers, signedApps, clientCredits, tendency);

  // Empty state: no credits ever
  if (clientCredits.length === 0 && clientApps.length === 0 && clientOffers.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Este cliente no tiene actividad crediticia registrada.
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3">
      {/* Hero row */}
      <div className="grid gap-3 lg:grid-cols-[1fr_1.2fr]">
        <CapacityHero capacity={capacity} level={profile?.credit_level} />
        <HealthHero score={healthGlobal} count={clientCredits.length} tendency={tendency} />
      </div>

      {/* Detail row */}
      <div className="grid gap-3 lg:grid-cols-[1fr_1.2fr] lg:items-start">
        <FunnelCard
          offerCount={clientOffers.length}
          totalOffers={totalOffers}
          appCount={signedApps.length}
          totalApps={totalApps}
          creditCount={clientCredits.length}
          totalDisbursed={totalDisbursed}
          totalCobrado={totalCobrado}
          totalFuture={totalFuture}
          totalOverdue={totalOverdue}
          totalPending={totalPending}
          offerToAppDrop={offerToAppDrop}
          appToCreditDrop={appToCreditDrop}
        />

        <div className="rounded-md border border-border bg-card p-3">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Salud crediticia por crédito
          </p>
          {clientCredits.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Sin créditos.</p>
          ) : (
            <ul className="flex flex-col">
              {[...clientCredits]
                .sort((a, b) => (a.disbursed_at ?? '').localeCompare(b.disbursed_at ?? ''))
                .map((credit, idx) => (
                  <CreditRow key={credit.credit_id} credit={credit} index={idx + 1} />
                ))}
            </ul>
          )}

          {nextPayment && <NextPaymentBanner payment={nextPayment} />}

          <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-2 text-[10px] text-muted-foreground">
            <LegendDot color="bg-green-500" label="90-100 puntual" />
            <LegendDot color="bg-lime-500" label="70-89 leve" />
            <LegendDot color="bg-yellow-500" label="40-69 frecuente" />
            <LegendDot color="bg-orange-500" label="20-39 grave" />
            <LegendDot color="bg-red-500" label="0-19 default" />
          </div>
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="rounded-r-md border border-border border-l-4 border-l-amber-500 bg-card p-4">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-500">
            <Lightbulb size={14} /> Observaciones del cliente
          </div>
          <ul className="space-y-1.5">
            {insights.map((insight, idx) => (
              <li key={idx} className="flex gap-2 text-sm leading-relaxed text-foreground">
                <span className="text-amber-500">•</span>
                <span dangerouslySetInnerHTML={{ __html: insight.html }} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Hero cards ────────────────────────────────────────────────────────────

function CapacityHero({ capacity, level }: { capacity: number; level: string | null | undefined }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-green-500/30 bg-gradient-to-br from-green-500/15 to-green-500/5 px-4 py-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Capacidad actual
        </p>
        <p className="mt-0.5 text-[10px] text-muted-foreground">
          basada en score · {level ?? '—'}
        </p>
      </div>
      <div className="text-right">
        <p className="text-3xl font-extrabold tabular-nums text-green-500">
          {formatCurrency(capacity, 'MXN', 'es-MX')}
        </p>
      </div>
    </div>
  );
}

function HealthHero({
  score,
  count,
  tendency,
}: {
  score: number;
  count: number;
  tendency: ReturnType<typeof computeTendency>;
}) {
  const color = scoreColor(score);
  const trendInfo = tendency
    ? tendency.dir === 'up'
      ? { className: 'text-green-500 bg-green-500/12', icon: <TrendingUp size={12} />, text: `+${tendency.delta}` }
      : tendency.dir === 'down'
      ? { className: 'text-red-400 bg-red-500/12', icon: <TrendingDown size={12} />, text: `-${tendency.delta}` }
      : { className: 'text-muted-foreground bg-muted', icon: <Minus size={12} />, text: 'estable' }
    : null;

  return (
    <div
      className={`flex items-center justify-between rounded-md border px-4 py-3 ${color.text === 'text-green-500'
        ? 'border-green-500/30 bg-gradient-to-br from-green-500/15 to-green-500/5'
        : color.text === 'text-lime-500'
        ? 'border-lime-500/30 bg-gradient-to-br from-lime-500/15 to-lime-500/5'
        : color.text === 'text-yellow-500'
        ? 'border-yellow-500/30 bg-gradient-to-br from-yellow-500/15 to-yellow-500/5'
        : color.text === 'text-orange-500'
        ? 'border-orange-500/30 bg-gradient-to-br from-orange-500/15 to-orange-500/5'
        : 'border-red-500/30 bg-gradient-to-br from-red-500/15 to-red-500/5'
      }`}
    >
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Salud crediticia
        </p>
        <p className="mt-0.5 text-[10px] text-muted-foreground">
          {count > 0 ? `promedio ponderado · ${count} ${count === 1 ? 'crédito' : 'créditos'}` : 'sin créditos'}
        </p>
      </div>
      <div className="text-right">
        <div className="flex items-center justify-end gap-2">
          <p className={`text-3xl font-extrabold tabular-nums ${color.text}`}>
            {count > 0 ? Math.round(score) : '—'}
          </p>
          {trendInfo && (
            <span
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-bold ${trendInfo.className}`}
            >
              {trendInfo.icon} {trendInfo.text}
            </span>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground">de 100</p>
      </div>
    </div>
  );
}

// ── Funnel ────────────────────────────────────────────────────────────────

function FunnelCard(props: {
  offerCount: number;
  totalOffers: number;
  appCount: number;
  totalApps: number;
  creditCount: number;
  totalDisbursed: number;
  totalCobrado: number;
  totalFuture: number;
  totalOverdue: number;
  totalPending: number;
  offerToAppDrop: number;
  appToCreditDrop: number;
}) {
  const { totalOffers, totalApps, totalDisbursed, totalCobrado, totalFuture, totalOverdue } = props;
  const totalToShow = Math.max(totalOffers, 1);

  function pct(v: number) {
    return ((v / totalToShow) * 100).toFixed(0);
  }
  function relPct(v: number, base: number) {
    return base > 0 ? ((v / base) * 100).toFixed(0) : '0';
  }

  const cobradoPct = totalDisbursed > 0 ? (totalCobrado / totalDisbursed) * 100 : 0;
  const futurePct = totalDisbursed > 0 ? (totalFuture / totalDisbursed) * 100 : 0;
  const overduePct = totalDisbursed > 0 ? (totalOverdue / totalDisbursed) * 100 : 0;

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Funnel acumulado
      </p>

      <FunnelStage
        label="Ofertas"
        sub={`${props.offerCount} ${props.offerCount === 1 ? 'recibida' : 'recibidas'}`}
        widthPct={100}
        amount={totalOffers}
        pct="100%"
      />
      <Dropoff
        amount={props.offerToAppDrop}
        text={
          props.offerToAppDrop > 0
            ? `Ofertas no convertidas en solicitud · -${formatCurrency(props.offerToAppDrop, 'MXN', 'es-MX')} (${(100 - parseFloat(pct(totalApps))).toFixed(0)}%)`
            : 'Todas las ofertas avanzaron a solicitud · $0'
        }
      />

      <FunnelStage
        label="Solicitudes"
        sub={`${props.appCount} firmadas`}
        widthPct={parseFloat(pct(totalApps))}
        amount={totalApps}
        pct={`${pct(totalApps)}%`}
      />
      <Dropoff
        amount={props.appToCreditDrop}
        text={
          props.appToCreditDrop > 0
            ? `Solicitudes no desembolsadas · -${formatCurrency(props.appToCreditDrop, 'MXN', 'es-MX')}`
            : 'Todas las solicitudes avanzaron a desembolso · $0'
        }
      />

      <FunnelStage
        label="Desembolsos"
        sub={`${props.creditCount} ejecutados`}
        widthPct={parseFloat(pct(totalDisbursed))}
        amount={totalDisbursed}
        pct={`${relPct(totalDisbursed, totalApps)}%`}
      />
      <Dropoff text="Pagos en curso" zero />

      {/* Estado del cobro: 3 segmentos (cobrado · por vencer · vencido) */}
      <div className="mb-1 grid grid-cols-[110px_1fr_90px] items-center gap-3">
        <div className="text-xs">
          <span className="font-medium text-foreground">Estado del cobro</span>
          <span className="block text-[10px] text-muted-foreground">
            de {formatCurrency(totalDisbursed, 'MXN', 'es-MX')}
          </span>
        </div>
        <div className="h-[22px] overflow-hidden rounded bg-[#0f172a]">
          <div className="flex h-full" style={{ width: `${pct(totalDisbursed)}%` }}>
            {cobradoPct > 0 && (
              <div
                className="flex h-full items-center bg-blue-500 px-2 text-[10px] font-semibold text-white"
                style={{ width: `${cobradoPct}%` }}
                title={`Cobrado: ${formatCurrency(totalCobrado, 'MXN', 'es-MX')}`}
              >
                {cobradoPct >= 12 ? formatCurrency(totalCobrado, 'MXN', 'es-MX') : ''}
              </div>
            )}
            {futurePct > 0 && (
              <div
                className="flex h-full items-center bg-green-500 px-2 text-[10px] font-semibold text-white"
                style={{ width: `${futurePct}%` }}
                title={`Por vencer: ${formatCurrency(totalFuture, 'MXN', 'es-MX')}`}
              >
                {futurePct >= 12 ? formatCurrency(totalFuture, 'MXN', 'es-MX') : ''}
              </div>
            )}
            {overduePct > 0 && (
              <div
                className="flex h-full items-center bg-red-500 px-2 text-[10px] font-semibold text-white"
                style={{ width: `${overduePct}%` }}
                title={`Vencido sin pagar: ${formatCurrency(totalOverdue, 'MXN', 'es-MX')}`}
              >
                {overduePct >= 12 ? formatCurrency(totalOverdue, 'MXN', 'es-MX') : ''}
              </div>
            )}
          </div>
        </div>
        <div className="text-right text-sm font-bold tabular-nums text-foreground">
          {cobradoPct.toFixed(0)}%
          <span className="block text-[10px] font-medium text-muted-foreground">cobrado</span>
        </div>
      </div>

      {/* Leyenda de la barra */}
      <div className="ml-[120px] flex flex-wrap gap-3 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm bg-blue-500" />
          Cobrado {formatCurrency(totalCobrado, 'MXN', 'es-MX')}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm bg-green-500" />
          Por vencer {formatCurrency(totalFuture, 'MXN', 'es-MX')}
        </span>
        {totalOverdue > 0 && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-red-500" />
            Vencido {formatCurrency(totalOverdue, 'MXN', 'es-MX')}
          </span>
        )}
      </div>
    </div>
  );
}

function FunnelStage({
  label,
  sub,
  widthPct,
  amount,
  pct,
}: {
  label: string;
  sub: string;
  widthPct: number;
  amount: number;
  pct: string;
}) {
  return (
    <div className="mb-2 grid grid-cols-[110px_1fr_90px] items-center gap-3">
      <div className="text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="block text-[10px] text-muted-foreground">{sub}</span>
      </div>
      <div className="h-[22px] overflow-hidden rounded bg-[#0f172a]">
        <div
          className="flex h-full items-center rounded bg-blue-500 px-2 text-[10px] font-semibold text-white"
          style={{ width: `${Math.max(0, Math.min(100, widthPct))}%` }}
        >
          {formatCurrency(amount, 'MXN', 'es-MX')}
        </div>
      </div>
      <div className="text-right text-sm font-bold tabular-nums text-foreground">
        {formatCurrency(amount, 'MXN', 'es-MX')}
        <span className="block text-[11px] font-medium text-muted-foreground">{pct}</span>
      </div>
    </div>
  );
}

function Dropoff({ amount = 0, text, zero }: { amount?: number; text: string; zero?: boolean }) {
  const isZero = zero || amount === 0;
  return (
    <div
      className={`-mt-0.5 mb-1.5 flex justify-end pr-[100px] text-[11px] font-medium ${isZero ? 'text-muted-foreground' : 'text-red-300'}`}
    >
      <span className="mr-1 font-bold">↓</span>
      {text}
    </div>
  );
}

// ── Credit row ────────────────────────────────────────────────────────────

function CreditRow({ credit, index }: { credit: BackofficeCreditsListItem; index: number }) {
  const score = creditScore(credit);
  const color = scoreColor(score);
  const badge = statusBadge(credit.status);
  const weeksPaid = credit.weeks_paid ?? 0;
  const weeksTotal = credit.weeks_total ?? credit.term_weeks ?? 0;

  return (
    <li className="grid cursor-pointer grid-cols-[4px_1fr_auto_auto] items-center gap-2.5 border-b border-border/30 py-2 pr-1 transition-colors last:border-b-0 hover:bg-accent/20">
      <div className={`h-8 w-1 rounded-sm ${color.bar}`} />
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-foreground">
          <span>Crédito #{index}</span>
          <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${badge.className}`}>
            {badge.label}
          </span>
          <span className="font-mono text-[10px] font-normal text-muted-foreground">
            {credit.credit_id.slice(0, 12)}
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground">
          {formatCurrency(credit.amount_mxn, 'MXN', 'es-MX')} · {credit.term_weeks} sem
          {weeksTotal > 0 && ` · ${weeksPaid}/${weeksTotal} cuotas pagadas`}
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-xs">
        <div className={`w-8 text-right text-base font-extrabold tabular-nums ${color.text}`}>
          {Math.round(score)}
        </div>
        <div className="flex flex-col items-end text-[9px] leading-none text-muted-foreground">
          <span>{color.label.split(' ')[0] ?? ''}</span>
          <span>{color.label.split(' ').slice(1).join(' ') || ''}</span>
        </div>
      </div>
      <ChevronRight size={14} className="text-muted-foreground" />
    </li>
  );
}

// ── Next payment banner ───────────────────────────────────────────────────

interface NextPaymentInfo {
  creditIndex: number;
  weeksPaid: number;
  weeksTotal: number;
  amount: number;
  dueDate: string;
  days: number;
  classification: 'future' | 'soon' | 'overdue-soft' | 'overdue' | 'default';
}

function NextPaymentBanner({ payment }: { payment: NextPaymentInfo }) {
  const styles = {
    future: 'border-blue-500 bg-blue-500/12',
    soon: 'border-green-500 bg-green-500/12',
    'overdue-soft': 'border-yellow-500 bg-yellow-500/14',
    overdue: 'border-orange-500 bg-orange-500/14',
    default: 'border-red-600 bg-red-600/22 border-l-[4px]',
  } as const;
  const labelStyles = {
    future: 'text-blue-400',
    soon: 'text-green-400',
    'overdue-soft': 'text-yellow-400',
    overdue: 'text-orange-400',
    default: 'text-red-300',
  } as const;
  const labels = {
    future: 'Próxima cuota',
    soon: 'Próxima cuota — por vencer',
    'overdue-soft': 'Cuota vencida',
    overdue: 'Cuota vencida',
    default: 'Crédito en default',
  } as const;

  const dueDate = new Date(payment.dueDate).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  const showWarn = payment.classification !== 'future' && payment.classification !== 'soon';

  let dateLine: string;
  if (payment.days >= 0) {
    dateLine = `vence ${dueDate} · en ${payment.days} ${payment.days === 1 ? 'día' : 'días'}`;
  } else {
    const overdue = -payment.days;
    dateLine = `venció ${dueDate} · hace ${overdue} ${overdue === 1 ? 'día' : 'días'}`;
  }

  return (
    <div className={`mt-3 flex items-center justify-between rounded-r border-l-[3px] px-3 py-2 text-xs ${styles[payment.classification]}`}>
      <div>
        <div className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide ${labelStyles[payment.classification]}`}>
          {showWarn && <AlertTriangle size={11} />}
          {labels[payment.classification]}
        </div>
        <div className="mt-0.5 text-foreground">
          Crédito #{payment.creditIndex} · cuota {payment.weeksPaid + 1} de {payment.weeksTotal}
        </div>
      </div>
      <div className="text-right">
        <div className="font-bold text-foreground">{formatCurrency(payment.amount, 'MXN', 'es-MX')}</div>
        <div className={`mt-0.5 text-[11px] ${labelStyles[payment.classification]}`}>{dateLine}</div>
      </div>
    </div>
  );
}

// ── Helpers de cómputo (locales — los compartidos viven en utils/creditHealth) ───

function findNextPayment(credits: BackofficeCreditsListItem[]): NextPaymentInfo | null {
  const sorted = [...credits].sort((a, b) => (a.disbursed_at ?? '').localeCompare(b.disbursed_at ?? ''));
  const activeIdx = sorted.findIndex((c) => c.status?.toUpperCase() === 'ACTIVE' || c.status?.toUpperCase() === 'DELINQUENT' || c.status?.toUpperCase() === 'DEFAULTED');
  if (activeIdx === -1) return null;
  const credit = sorted[activeIdx]!;
  if (!credit.next_due_date) return null;
  const days = daysUntil(credit.next_due_date);
  const status = credit.status?.toUpperCase();

  let classification: NextPaymentInfo['classification'];
  if (status === 'DEFAULTED') classification = 'default';
  else if (days > 3) classification = 'future';
  else if (days >= 0) classification = 'soon';
  else {
    const overdue = -days;
    if (overdue <= 7) classification = 'overdue-soft';
    else if (overdue <= 30) classification = 'overdue';
    else classification = 'default';
  }

  return {
    creditIndex: activeIdx + 1,
    weeksPaid: credit.weeks_paid ?? 0,
    weeksTotal: credit.weeks_total ?? credit.term_weeks ?? 0,
    amount: credit.weekly_payment_mxn ?? 0,
    dueDate: credit.next_due_date,
    days,
    classification,
  };
}

interface Insight {
  html: string;
}

function buildInsights(
  offers: BackofficeOffersListItem[],
  signedApps: BackofficeApplicationsListItem[],
  credits: BackofficeCreditsListItem[],
  tendency: Tendency,
): Insight[] {
  const insights: Insight[] = [];

  if (offers.length > 0 && signedApps.length > 0) {
    const conv = (signedApps.length / offers.length) * 100;
    const sentimentClass = conv >= 50 ? 'text-green-400' : 'text-red-300';
    insights.push({
      html: `Convirtió <b>${signedApps.length} de ${offers.length} ofertas</b> en solicitudes — <span class="${sentimentClass}">tasa de conversión ${conv.toFixed(0)}%</span>.`,
    });
  } else if (offers.length > 0 && signedApps.length === 0) {
    insights.push({
      html: `Recibió <b>${offers.length} oferta${offers.length === 1 ? '' : 's'}</b> pero no ha firmado ninguna solicitud.`,
    });
  }

  const sortedCredits = [...credits].sort((a, b) => (a.disbursed_at ?? '').localeCompare(b.disbursed_at ?? ''));
  const firstCredit = sortedCredits[0];
  if (firstCredit && firstCredit.status?.toUpperCase() === 'PAID_OFF') {
    insights.push({
      html: `Su <b>primer crédito</b> lo pagó completo — <span class="text-green-400">historial inicial impecable</span>.`,
    });
  }

  const activeCredit = credits.find((c) => c.status?.toUpperCase() === 'ACTIVE');
  if (activeCredit && activeCredit.weeks_paid && activeCredit.weeks_total) {
    const score = creditScore(activeCredit);
    const sent = score >= 70 ? 'text-green-400' : score >= 40 ? 'text-yellow-400' : 'text-red-300';
    insights.push({
      html: `En el crédito activo lleva <b>${activeCredit.weeks_paid} de ${activeCredit.weeks_total} cuotas</b> pagadas (salud <span class="${sent}">${Math.round(score)}/100</span>).`,
    });
  }

  if (tendency && tendency.dir !== 'flat') {
    const word = tendency.dir === 'up' ? 'mejorado' : 'empeorado';
    const sent = tendency.dir === 'up' ? 'text-green-400' : 'text-red-300';
    insights.push({
      html: `<b>Tendencia:</b> la salud crediticia ha <span class="${sent}">${word} ${tendency.delta} puntos</span> respecto al historial anterior.`,
    });
  }

  const defaulted = credits.find((c) => c.status?.toUpperCase() === 'DEFAULTED');
  if (defaulted) {
    insights.push({
      html: `<span class="text-red-300"><b>Cliente en default</b></span> en el crédito <span class="font-mono text-xs">${defaulted.credit_id.slice(0, 12)}</span>.`,
    });
  }

  return insights;
}
